import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { useSearchParams } from "react-router-dom";
import axiosInstant from "../../../../api";
import { SingleFilter } from "./singleFilter";
import { getVideoTag, videoTagVocabulary } from "../../../../api/endpoints";
import { IconTag } from "../../../../utils/icons";
import { ISingleOption, getFilterProps } from "./helper";

// Tag names by id, shared by the filter and the active-filter chip so a
// ?tagId= deep link resolves once even when it is outside the top 50.
const tagNames = new Map<string, string>();

export const useVideoTagName = (tagId: string) => {
    const [name, setName] = useState(() => tagNames.get(tagId) || "");

    useEffect(() => {
        if (!tagId) {
            setName("");
            return undefined;
        }
        const cached = tagNames.get(tagId);
        if (cached) {
            setName(cached);
            return undefined;
        }
        let cancelled = false;
        setName("");
        axiosInstant
            .get(getVideoTag(tagId))
            .then(({ data }) => {
                const text = data?.tag?.tagtext || "";
                if (text) tagNames.set(tagId, text);
                if (!cancelled) setName(text);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [tagId]);

    return name;
};

interface Props {
    onFilterSelect: (key: string, id?: string) => void;
    queryFilters?: any;
}

export const TagFilter: FC<Props> = ({ onFilterSelect, ...rest }) => {
    const [tags, setTags] = useState<Array<ISingleOption>>([]);
    const [search, setSearch] = useState("");
    const latest = useRef(0);
    const [searchParams] = useSearchParams();
    const selectedId = (rest.queryFilters ? rest.queryFilters.tagId : searchParams.get("tagId")) || "";
    const selectedName = useVideoTagName(selectedId);

    useDebounce(
        () => {
            latest.current += 1;
            const seq = latest.current;
            axiosInstant
                .get(videoTagVocabulary(), { params: { search, scope: "video" } })
                .then(({ data }) => {
                    if (seq !== latest.current) return;
                    const rows: ISingleOption[] = (data?.tags || []).map((t: any) => ({
                        id: String(t.tagid),
                        name: t.tagtext,
                    }));
                    rows.forEach((t) => tagNames.set(t.id, t.name));
                    setTags(rows);
                })
                .catch(() => undefined);
        },
        300,
        [search],
    );

    const onSearchChange = useCallback((text: string) => setSearch(text), []);

    const options = useMemo(() => {
        if (!selectedId || !selectedName || tags.some((t) => t.id === selectedId)) return tags;
        return [{ id: selectedId, name: selectedName }, ...tags];
    }, [tags, selectedId, selectedName]);

    return (
        <SingleFilter
            {...rest}
            {...getFilterProps(options, "tagId", "tag")}
            icon={IconTag}
            updateSearch={onFilterSelect}
            onSearchChange={onSearchChange}
        />
    );
};
